// =====================================================================
// MisExpenseDetail.jsx
// =====================================================================
// Per-property detail panel for the MIS Expense Tracker.
// Renders the property KPIs header, 6-month timeline strip, and the
// line-item expense breakdown table. Extracted from MisExpenses.jsx
// (iter-35e) to keep the page shell focused on data fetching + picker.
// =====================================================================

import { fmtINR, fmtL, fmtPct, momTone } from "./_format";
import { KPI } from "./mis_kpi";

export default function MisExpenseDetail({ data, latest, prev }) {
  return (
    <>
      {/* Property header + KPIs */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5">
        <header className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-teal-600">{data.property?.state || data.property?.type || ""}</div>
            <h2 className="font-display font-black text-2xl tracking-tight" data-testid="mis-expense-title">{data.location}</h2>
            <div className="text-[11.5px] text-zinc-500 mt-1">
              {data.property?.type || "—"} · {data.property?.model || "—"}
              {data.property?.start_date ? ` · since ${data.property.start_date}` : ""}
            </div>
          </div>
        </header>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <KPI label={latest?.month || "Latest"} value={fmtINR(latest?.revenue)} sub="Revenue" accent="#20B2AA" />
          <KPI label="Expense" value={fmtINR(latest?.expense)} sub={`${fmtPct(latest?.opex_ratio_pct)} of rev`} accent="#FFA500" />
          <KPI label="EBITDA" value={fmtINR(latest?.ebitda)} sub={fmtPct(latest?.ebitda_margin_pct)} accent="#FF69B4" tone={latest?.ebitda < 0 ? "rose" : "emerald"} />
          <KPI label="Occupancy"
               value={fmtPct(latest?.occ_total_pct)}
               sub={`D ${fmtPct(latest?.occ_dorm_pct)} · P ${fmtPct(latest?.occ_private_pct)}`}
               accent="#FFCC00" />
          <KPI label="Variable cost" value={fmtINR(latest?.variable_cost)} sub={`₹${fmtINR(latest?.variable_cost_per_bed_night).slice(1)}/bed-night`} accent="#FB7185" />
          <KPI label="Fixed cost" value={fmtINR(latest?.fixed_cost)} accent="#9CA3AF" />
          <KPI label="MoM Δ Expense"
               value={prev && prev.expense
                 ? `${((latest.expense - prev.expense) / prev.expense * 100).toFixed(1)}%`
                 : "—"}
               accent="#9370DB"
               tone={prev && latest && latest.expense > prev.expense ? "rose" : "emerald"} />
        </div>
      </section>

      {/* Timeline strip — last 6 months with occupancy mini-bar */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-expense-timeline">
        <header className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Last 6 months</div>
            <h3 className="font-display font-black text-base">Revenue · Expense · EBITDA · Occupancy</h3>
          </div>
          <div className="text-[10px] text-zinc-500">Hover-friendly trend strip</div>
        </header>
        <div className="grid grid-cols-6 gap-2">
          {(data.timeline || []).slice(-6).map((m) => (
            <div key={m.key} className="rounded-md border border-zinc-200 p-2 bg-zinc-50/60">
              <div className="text-[10px] uppercase font-bold text-zinc-500">{m.month}</div>
              <div className="text-[11px] text-emerald-700 tabular-nums mt-1">↑ {fmtL(m.revenue)}</div>
              <div className="text-[11px] text-rose-700 tabular-nums">↓ {fmtL(m.expense)}</div>
              <div className={`text-[11px] font-bold tabular-nums mt-0.5 ${m.ebitda < 0 ? "text-rose-800" : "text-emerald-800"}`}>= {fmtL(m.ebitda)}</div>
              {/* Occupancy bar */}
              <div className="mt-1.5">
                <div className="text-[9.5px] text-zinc-500 flex justify-between">
                  <span>Occ</span><span className="font-bold tabular-nums">{m.occ_total_pct ? m.occ_total_pct.toFixed(0) : 0}%</span>
                </div>
                <div className="h-1 bg-zinc-200 rounded overflow-hidden mt-0.5">
                  <div className="h-full" style={{ width: `${Math.min(m.occ_total_pct || 0, 100)}%`, background: "#FF69B4" }} />
                </div>
              </div>
              <div className="text-[9.5px] text-zinc-500 mt-1">{m.opex_ratio_pct}% opex</div>
            </div>
          ))}
        </div>
      </section>

      {/* Line-item table */}
      <section className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="mis-expense-table">
        <header className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-zinc-500">Line items · {latest?.month}</div>
            <h3 className="font-display font-black text-base">Expense breakdown by head</h3>
          </div>
          <div className="flex items-center gap-3 text-[10.5px]">
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-rose-400" /><span className="text-zinc-600">VAR (occ-linked)</span></span>
            <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-400" /><span className="text-zinc-600">FIX</span></span>
          </div>
        </header>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="py-2">Category</th>
              <th className="py-2">Type</th>
              <th className="py-2 text-right">Latest</th>
              <th className="py-2 text-right">Prev</th>
              <th className="py-2 text-right">6mo avg</th>
              <th className="py-2 text-right">Share</th>
              <th className="py-2 text-right">MoM Δ</th>
            </tr>
          </thead>
          <tbody>
            {(data.categories || []).map((c) => {
              const t = momTone(c.mom_pct);
              const Icon = t.icon;
              return (
                <tr key={c.category} className="border-b border-zinc-100" data-testid={`mis-expense-row-${c.category.replace(/\W/g, "")}`}>
                  <td className="py-2 font-medium text-zinc-800">{c.category}</td>
                  <td className="py-2">
                    <span className={`text-[9.5px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded ${c.is_variable ? "bg-rose-50 text-rose-700" : "bg-zinc-100 text-zinc-600"}`}>
                      {c.is_variable ? "VAR" : "FIX"}
                    </span>
                  </td>
                  <td className="py-2 text-right tabular-nums font-bold text-zinc-900">{fmtINR(c.latest)}</td>
                  <td className="py-2 text-right tabular-nums text-zinc-500">{fmtINR(c.prev)}</td>
                  <td className="py-2 text-right tabular-nums text-zinc-500">{fmtINR(c.avg6)}</td>
                  <td className="py-2 text-right tabular-nums">
                    <span className="inline-block w-16 h-1 rounded bg-zinc-100 mr-2 overflow-hidden align-middle">
                      <span className="block h-full rounded" style={{ width: `${Math.min(c.share_of_total_pct, 100)}%`, background: c.is_variable ? "#FB7185" : "#20B2AA" }} />
                    </span>
                    <span className="text-zinc-700">{c.share_of_total_pct}%</span>
                  </td>
                  <td className="py-2 text-right">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${t.bg} ${t.text} tabular-nums font-bold text-[11px]`}>
                      <Icon className="w-3 h-3" />
                      {c.mom_pct >= 0 ? "+" : ""}{c.mom_pct}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </>
  );
}
